import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './Login';
import Manager from './Manager';
import Schedule from './Schedule';
import Dashboard from './Dashboard';
import Profile from './Profile';
import SuperAdmin from './SuperAdmin';
import { getCurrentUser, signOut } from './auth';
import Payment from './Payment';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || 'YOUR_API_ENDPOINT_HERE';

const getTodayDate = () => new Date().toISOString().split('T')[0];

const formatTime = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return `${h}h ${m}m`;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [idToken, setIdToken] = useState(null);
  const [isManager, setIsManager] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [showProfile, setShowProfile] = useState(false);
  const [balance, setBalance] = useState(null);
  const [entries, setEntries] = useState([]);
  const [payments, setPayments] = useState([]);
  const [allEntries, setAllEntries] = useState([]);
  const [vehicleId, setVehicleId] = useState('');
  const [availableAircraft, setAvailableAircraft] = useState([]);
  const [selectedAircraft, setSelectedAircraft] = useState('');
  const [showAllPersonal, setShowAllPersonal] = useState(false);
  const [showAllVehicle, setShowAllVehicle] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [entry, setEntry] = useState({
    date: getTodayDate(),
    tachoStart: '',
    tachoFinish: '',
    fuelLitres: '',
    fuelCost: '',
    oilQuarts: '',
    oilCost: '',
    receipt: null
  });
  const [payment, setPayment] = useState({ date: getTodayDate(), amount: '' });

  useEffect(() => {
    console.log('Checking for current user...');
    getCurrentUser()
      .then(({ idToken, session }) => {
        console.log('Got current user, session:', session);
        console.log('ID Token payload:', session.getIdToken().payload);
        setIdToken(idToken);
        setIsAuthenticated(true);
        
        const payload = session.getIdToken().payload;
        const groups = payload['cognito:groups'] || [];
        console.log('User groups from payload:', groups);
        console.log('Groups type:', typeof groups);
        const hasManagerRole = Array.isArray(groups) ? groups.some(g => g.endsWith('-manager')) : false;
        const hasSuperAdminRole = Array.isArray(groups) ? groups.includes('super-admin') : false;
        console.log('Is manager:', hasManagerRole);
        console.log('Is super admin:', hasSuperAdminRole);
        setIsManager(hasManagerRole);
        setIsSuperAdmin(hasSuperAdminRole);
        
        // Extract available aircraft
        const aircraft = Array.isArray(groups) 
          ? groups.filter(g => !g.endsWith('-manager') && g !== 'super-admin').map(g => g.replace('-manager', ''))
          : [];
        setAvailableAircraft(aircraft);
        
        // Set default aircraft from localStorage or first available
        const saved = localStorage.getItem('selectedAircraft');
        if (saved && aircraft.includes(saved)) {
          setSelectedAircraft(saved);
        } else if (aircraft.length > 0) {
          setSelectedAircraft(aircraft[0]);
          localStorage.setItem('selectedAircraft', aircraft[0]);
        }
      })
      .catch((err) => {
        console.log('No current user found:', err);
        setIsAuthenticated(false);
      });
  }, []);

  const fetchBalance = useCallback(async () => {
    if (!idToken || !selectedAircraft) return;
    try {
      const res = await fetch(`${API_ENDPOINT}/balance?vehicleId=${selectedAircraft}`, {
        headers: { 'Authorization': idToken }
      });
      const data = await res.json();
      setBalance(data);
      if (data.vehicleId) {
        setVehicleId(data.vehicleId);
      }
      if (data.entries) {
        setEntries(data.entries.sort((a, b) => b.timestamp - a.timestamp));
      }
      if (data.payments) {
        setPayments(data.payments);
      }
      if (data.allEntries) {
        setAllEntries(data.allEntries.sort((a, b) => {
          if (a.tachoFinish !== b.tachoFinish) {
            return a.tachoFinish - b.tachoFinish;
          }
          return new Date(a.date) - new Date(b.date);
        }));
      }
    } catch (err) {
      console.error('Error fetching balance:', err);
    }
  }, [idToken, selectedAircraft]);

  useEffect(() => {
    if (isAuthenticated && selectedAircraft) {
      fetchBalance();
    }
  }, [isAuthenticated, selectedAircraft, fetchBalance]);

  const handleAircraftChange = (e) => {
    const aircraft = e.target.value;
    setSelectedAircraft(aircraft);
    localStorage.setItem('selectedAircraft', aircraft);
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEntry({...entry, receipt: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEntrySubmit = async (e) => {
    e.preventDefault();
    
    if (parseFloat(entry.tachoFinish) <= parseFloat(entry.tachoStart)) {
      alert('Tacho Finish must be greater than Tacho Start');
      return;
    }
    
    if ((parseFloat(entry.fuelCost) > 0 || parseFloat(entry.oilCost) > 0) && !entry.receipt) {
      alert('Receipt required for fuel or oil entries');
      return;
    }
    
    try {
      const res = await fetch(`${API_ENDPOINT}/entries`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify({...entry, vehicleId: selectedAircraft})
      });
      const data = await res.json();
      console.log('Entry response:', data);
      if (!res.ok) {
        alert('Error adding entry: ' + (data.message || res.statusText));
        return;
      }
      alert('Entry added successfully!');
      setEntry({ date: getTodayDate(), tachoStart: '', tachoFinish: '', fuelLitres: '', fuelCost: '', oilQuarts: '', oilCost: '', receipt: null });
      fetchBalance();
    } catch (err) {
      console.error('Error adding entry:', err);
      alert('Error: ' + err.message);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_ENDPOINT}/payments`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': idToken
        },
        body: JSON.stringify({ amount: parseFloat(payment.amount), date: payment.date, vehicleId: selectedAircraft })
      });
      const data = await res.json();
      console.log('Payment response:', data);
      if (!res.ok) {
        alert('Error adding payment: ' + (data.message || res.statusText));
        return;
      }
      alert('Payment added successfully!');
      setPayment({ date: getTodayDate(), amount: '' });
      fetchBalance();
    } catch (err) {
      console.error('Error adding payment:', err);
      alert('Error: ' + err.message);
    }
  };

  const handleLogin = (token) => {
    console.log('handleLogin called with token');
    setIdToken(token);
    setIsAuthenticated(true);
    
    // Force page reload to trigger getCurrentUser with new session
    window.location.reload();
  };

  const handleLogout = () => {
    signOut();
    setIsAuthenticated(false);
    setIdToken(null);
    setBalance(null);
    setEntries([]);
    setCurrentView('dashboard');
  };

  const filterLast6Months = (entries) => {
    const sixMonthsAgo = new Date();
    sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
    return entries.filter(entry => new Date(entry.date) >= sixMonthsAgo);
  };

  const displayedPersonalEntries = showAllPersonal ? entries : filterLast6Months(entries);
  const displayedVehicleEntries = showAllVehicle ? allEntries : filterLast6Months(allEntries);

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (showProfile) {
    return <Profile onClose={() => setShowProfile(false)} />;
  }

  if (currentView === 'manager') {
    return <Manager idToken={idToken} onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'super-admin') {
    return <SuperAdmin idToken={idToken} onBack={() => setCurrentView('dashboard')} />;
  }

  if (currentView === 'schedule') {
    return (
      <div className="App">
        <div className="header">
          <h1>Aircraft Schedule</h1>
          <div className="header-buttons">
            <button onClick={() => setShowProfile(true)} className="profile-btn" title="Profile">
              <span className="profile-icon">👤</span>
            </button>
            <button onClick={() => setCurrentView('dashboard')} className="back-btn">Back</button>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        </div>
        <Schedule apiEndpoint={API_ENDPOINT} idToken={idToken} isManager={isManager} />
      </div>
    );
  }

  if (currentView === 'dashboard') {
    return (
      <div className="App">
        <div className="header">
          <h1>Aircraft Tracking</h1>
          <div className="header-buttons">
            {availableAircraft.length > 1 && (
              <select value={selectedAircraft} onChange={handleAircraftChange} className="aircraft-selector">
                {availableAircraft.map(ac => <option key={ac} value={ac}>{ac}</option>)}
              </select>
            )}
            <button onClick={() => setShowProfile(true)} className="profile-btn" title="Profile">
              <span className="profile-icon">👤</span>
            </button>
            <button onClick={handleLogout} className="logout-btn">Logout</button>
          </div>
        </div>
        <Dashboard
          balance={balance}
          apiEndpoint={API_ENDPOINT}
          idToken={idToken}
          onNavigate={setCurrentView}
          isManager={isManager}
          isSuperAdmin={isSuperAdmin}
          onPayNow={() => setShowPayment(true)}
        />
        {showPayment && (
          <Payment
            apiEndpoint={API_ENDPOINT}
            idToken={idToken}
            vehicleId={selectedAircraft}
            balance={balance?.balance || 0}
            entries={entries}
            onClose={() => setShowPayment(false)}
            onPaymentComplete={fetchBalance}
          />
        )}
      </div>
    );
  }

  return (
    <div className="App">
      <div className="header">
        <h1>Aircraft Tracking</h1>
        <div className="header-buttons">
          {availableAircraft.length > 1 && (
            <select value={selectedAircraft} onChange={handleAircraftChange} className="aircraft-selector">
              {availableAircraft.map(ac => <option key={ac} value={ac}>{ac}</option>)}
            </select>
          )}
          <button onClick={() => setCurrentView('schedule')} className="schedule-btn">Schedule</button>
          {isManager && (
            <button onClick={() => setCurrentView('manager')} className="manager-btn">Manager Dashboard</button>
          )}
          <button onClick={() => setCurrentView('dashboard')} className="back-btn">Dashboard</button>
          <button onClick={() => setShowProfile(true)} className="profile-btn" title="Profile">
            <span className="profile-icon">👤</span>
          </button>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </div>
      
      {balance && (
        <div className="balance-card">
          <h2>Current Balance: £{balance.balance.toFixed(2)}</h2>
          <p>Total Flight Costs: £{balance.totalEntries.toFixed(2)}</p>
          <p>Total Payments: £{balance.totalPayments.toFixed(2)}</p>
        </div>
      )}

      <div className="forms-container">
        <form onSubmit={handleEntrySubmit} className="form-card">
          <h3>Add Tacho Entry</h3>
          <input type="date" value={entry.date} onChange={(e) => setEntry({...entry, date: e.target.value})} required />
          <input type="number" step="0.01" placeholder="Tacho Start" value={entry.tachoStart} onChange={(e) => setEntry({...entry, tachoStart: e.target.value})} required />
          <input type="number" step="0.01" placeholder="Tacho Finish" value={entry.tachoFinish} onChange={(e) => setEntry({...entry, tachoFinish: e.target.value})} required />
          {entry.tachoStart && entry.tachoFinish && (
            <div className="time-display">
              <strong>Hours:</strong> {(entry.tachoFinish - entry.tachoStart).toFixed(2)} ({formatTime(entry.tachoFinish - entry.tachoStart)})
            </div>
          )}
          <input type="number" step="0.01" placeholder="Fuel Litres" value={entry.fuelLitres} onChange={(e) => setEntry({...entry, fuelLitres: e.target.value})} />
          <input type="number" step="0.01" placeholder="Fuel Cost (£)" value={entry.fuelCost} onChange={(e) => setEntry({...entry, fuelCost: e.target.value})} />
          <input type="number" step="0.01" placeholder="Oil Quarts" value={entry.oilQuarts} onChange={(e) => setEntry({...entry, oilQuarts: e.target.value})} />
          <input type="number" step="0.01" placeholder="Oil Cost (£)" value={entry.oilCost} onChange={(e) => setEntry({...entry, oilCost: e.target.value})} />
          {(parseFloat(entry.fuelCost) > 0 || parseFloat(entry.oilCost) > 0) && (
            <div className="receipt-upload">
              <label>Receipt (Required) *</label>
              <input type="file" accept="image/*" onChange={handleReceiptChange} required />
              {entry.receipt && <span className="receipt-indicator">✓ Receipt attached</span>}
            </div>
          )}
          <button type="submit">Submit Entry</button>
        </form>

        <form onSubmit={handlePaymentSubmit} className="form-card">
          <h3>Add Payment</h3>
          <input type="date" value={payment.date} onChange={(e) => setPayment({...payment, date: e.target.value})} required />
          <input type="number" step="0.01" placeholder="Payment Amount (£)" value={payment.amount} onChange={(e) => setPayment({...payment, amount: e.target.value})} required />
          <button type="submit">Submit Payment</button>
        </form>
      </div>

      {entries.length > 0 && (
        <div className="entries-log">
          <div className="log-header">
            <h3>Personal Tacho Log</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Tacho Start</th>
                <th>Tacho Finish</th>
                <th>Hours (Decimal)</th>
                <th>Time (H:M)</th>
                <th>Fuel Cost</th>
                <th>Oil Cost</th>
                <th>Flight Cost</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {displayedPersonalEntries.map((entry, idx) => {
                const hours = entry.tachoFinish - entry.tachoStart;
                return (
                  <tr key={idx}>
                    <td>{entry.date}</td>
                    <td>{entry.tachoStart}</td>
                    <td>{entry.tachoFinish}</td>
                    <td>{hours.toFixed(2)}</td>
                    <td>{formatTime(hours)}</td>
                    <td>£{entry.fuelCost.toFixed(2)}</td>
                    <td>£{entry.oilCost.toFixed(2)}</td>
                    <td>£{entry.entryCost.toFixed(2)}</td>
                    <td>{entry.hasReceipt ? '✓' : '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {payments.length > 0 && (
        <div className="entries-log">
          <div className="log-header">
            <h3>Payment History</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Description</th>
                <th>Status</th>
                <th>Reference</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p, idx) => (
                <tr key={idx}>
                  <td>{p.date}</td>
                  <td>£{p.amount.toFixed(2)}</td>
                  <td style={{textTransform:'capitalize'}}>{p.paymentMethod === 'stripe' ? 'Card/Bank' : 'Manual'}</td>
                  <td>{p.description || '-'}</td>
                  <td>
                    <span className={`payment-status-badge ${p.status}`}>
                      {p.status}
                    </span>
                  </td>
                  <td style={{fontSize:'11px',color:'#999'}}>{p.stripePaymentIntentId || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {allEntries.length > 0 && (
        <div className="entries-log">
          <div className="log-header">
            <h3>Aircraft {vehicleId} Tacho Log (All Users)</h3>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Tacho Start</th>
                <th>Tacho Finish</th>
                <th>Fuel Added (Litres)</th>
              </tr>
            </thead>
            <tbody>
              {displayedVehicleEntries.map((entry, idx) => (
                <tr key={idx}>
                  <td>{entry.date}</td>
                  <td>{entry.tachoStart}</td>
                  <td>{entry.tachoFinish}</td>
                  <td>{entry.fuelLitres}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default App;
